import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommandClickEventArgs, CommandModel, EditService, FilterService, GridModule, PageService, SortService, ToolbarService } from '@syncfusion/ej2-angular-grids';
import { UserManagementService } from './user-management.service';
import { AvpCustomAdaptor } from '../shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import {
  ClientDTO,
  CreateInterfaceUserDTO,
  CreateUserDTO,
  UpdateInterfaceUserInfoDTO,
  UpdateUserInfoDTO,
  Role
} from './../models/models.swagger.generated';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserInfoDTO } from '../models/models.swagger.generated';
import { AuthService } from '../services/auth.service';
import { passwordValidation } from 'src/app/directives/password-validation.directive';
import { RoleService } from '../services/role.service';
import { EnumFilterService } from '../services/enum-filter.service';
import { UserEditFormType } from '../user-edit-form/user-edit-form-type';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { UserEditFormComponent } from '../user-edit-form/user-edit-form.component';
import { TitleBarComponent } from '../shared/title-bar/title-bar.component';
import { ClientAutocompleteComponent } from '../shared/client-autocomplete/client-autocomplete.component';
import { SearchFieldComponent } from '../shared/search-field/search-field.component';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { SfRequestTypes } from '../models/syncfusion-data';
import { EnumFilterDataManager } from '../shared/syncfusion/enum-filter-data-manager';

export type RoleEnumFilterType = {
  roles: Role;
}

@Component({
    selector: 'app-user-management',
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.scss'],
    imports: [TranslateModule, GridModule, CheckBoxModule, ToolbarModule, UserEditFormComponent, TitleBarComponent, ClientAutocompleteComponent, SearchFieldComponent],
    providers: [PageService, FilterService, ToolbarService, SortService, EditService,]
})

export class UserManagementComponent extends GridBaseComponent<UserEditFormType> implements OnInit {
  public languageFilter = GridConstantsFactory.createCheckBoxFilter();
  public roleFilter = GridConstantsFactory.createCheckBoxFilter();
  public noFilterColumnAttributes = { class: 'grid-no-filter' };

  public Role = Role;
  public editUserIsDomainuser = false;
  public rolesFieldName = 'roles';
  private rolesEnumFilterValues: RoleEnumFilterType[] = [];
  private addUserRole: Role;

  public customEditSettings = {
    allowEditing: true,
    allowAdding: true,
    allowDeleting: false,
    mode: 'Dialog',
    showDeleteConfirmDialog: true
  };

  public get selectedClient(): ClientDTO {
    return this.userManagementService.selectedClient;
  }

  public get canAddServiceUser(): boolean {
    return this.authService.isUserAdminOrConfigurationAdmin();
  }

  public get canSelectClients(): boolean {
    return this.authService.isUserAdminOrConfigurationAdmin();
  }

  public get isLoggedInUserAdmin(): boolean {
    return this.authService.getUserInfo().roles.includes(Role.Admin);
  }

  public get canAddAssetStorageGroupMember(): boolean {
    return this.authService.isUserAdminOrAssetStorageAdmin();
  }

  private get canLoggedInUserAdministrateServiceUsers(): boolean {
    return this.authService.isUserAdminOrConfigurationAdmin();
  }

  constructor(
    private fb: FormBuilder,
    private userManagementService: UserManagementService,
    translateService: TranslateService,
    public authService: AuthService,
    private spinnerService: SpinnerService,
    private roleService: RoleService,
    private enumFilterService: EnumFilterService) {
    super(translateService, userManagementService);
    this.gridCommands = [{
        type: 'Edit',
        buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'icon-edit e-icons'
        }
      },
      {
        title: 'Reset 2FA',
        type: 'None',
        buttonOption:
        {
          cssClass: 'icon-btn reset-2fa-btn',
          iconCss: 'stargate-icon-reset_2fa e-icons reset-2fa'
        }
      },
      {
        type: 'Delete',
        buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'icon-trash-can e-icons e-danger'
        }
      }
    ] as CommandModel[];
    this.searchOptions = { fields: ['firstname', 'lastname', 'client.name', 'email'], operator: 'startswith' };
  }

  public ngOnInit() {
    const enumType = Role;
    this.rolesEnumFilterValues = this.enumFilterService.createEnumFilterArray(enumType, this.rolesFieldName);
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.userManagementService, this.spinnerService)
    });

    this.isActiveColumnName = 'enabled';
    this.editDialogTitle = 'i18n.base.user-add-modal.titleEdit';
    this.addDialogTitle = 'i18n.base.user-add-modal.titleAdd';
    this.addIndexFindColumnName = 'userName';
  }

  public onClientSelected(client: ClientDTO) {
    this.userManagementService.selectedClient = client;
    this.grid?.refresh();
  }

  public addUser(userRole: Role) {
    this.addUserRole = userRole;
    this.onAdd();
  }

  public isTechnicalUser(userInfo: UserInfoDTO): boolean {
    return userInfo.roles?.some(r => Object.values(Role).includes(r));
  };

  public onRowDataBound(args: any) {
    const user = args.data as UserInfoDTO;
    if (args.data.roles) {
      if (this.roleService.roleMappingInfo.fixedUserRoles.indexOf(args.data.roles[0]) > -1) {
        // finding the button in the grid is tricky, need to fine better option
        var button = args.row.cells[args.row.cells.length - 1].getElementsByClassName('e-editbutton');
        if (button) {
          button[0].style.display = 'none';
        }
        var button = args.row.cells[args.row.cells.length - 1].getElementsByClassName('reset-2fa-btn');
        if (button) {
          button[0].style.display = 'none';
        }
      }
    }
    if (!user.twoFactorEnabled) {
      var button = args.row.cells[args.row.cells.length - 1].getElementsByClassName('reset-2fa-btn');
      if (button) {
        button[0].style.display = 'none';
      }
    }
    if (user.roles.includes(Role.InterfaceServiceAccess)) {
      if (!this.canLoggedInUserAdministrateServiceUsers) {
        var button = args.row.cells[args.row.cells.length - 1].getElementsByClassName('e-deletebutton');
        if (button) {
          button[0].style.display = 'none';
        }
      }
    } else if (!user.roles.includes(Role.AssetStorageGroupMember) || !this.isLoggedInUserAdmin) {
      var button = args.row.cells[args.row.cells.length - 1].getElementsByClassName('e-deletebutton');
      if (button) {
        button[0].style.display = 'none';
      }
    }
  }

  public formatRoles(userInfo: UserInfoDTO): string {
    if (!userInfo || !userInfo.roles) {
      return '';
    }
    return userInfo.roles.map(r => this.translateService.instant('i18n.shared.roles.' + Role[r])).join(', ');
  }

  public formatFilterRole(roleEnumFilter: RoleEnumFilterType): string {
    if (!roleEnumFilter || !roleEnumFilter.roles) {
      return '';
    }
    return this.translateService.instant('i18n.shared.roles.' + roleEnumFilter.roles);
  }

  public getClientName(client: ClientDTO): string {
    if (!client?.id) {
      return null;
    }
    return client.name + ' (' + (client.customerNumber ?? client.vendorNumber) + ')';
  }

  public commandClick(args: CommandClickEventArgs) {
    if (args.commandColumn?.type === 'None') {
      const user = args.rowData as UserInfoDTO;
      this.modalHelperService.confirm('i18n.base.user-management.messages.reset-2fa-confirm.title', 'i18n.base.user-management.messages.reset-2fa-confirm.message', () => {
        this.userManagementService.reset2FARegistration(user.userId).subscribe(() => {
          this.grid.refresh();
        });
      });
    }
    if (args.commandColumn?.type === 'Delete') {
      const user = args.rowData as UserInfoDTO;
      if (user.roles.some(r => r === Role.InterfaceServiceAccess)) {1
        this.modalHelperService.confirm('i18n.base.user-management.messages.delete-service-user-confirm.title', 'i18n.base.user-management.messages.delete-service-user-confirm.message', () => {
          this.userManagementService.deleteServiceUser(user.userId).subscribe(() => {
            this.grid.refresh();
          });
        });
      }
      if (user.roles.some(r => r === Role.AssetStorageGroupMember)) {
        this.modalHelperService.confirm('i18n.base.user-management.messages.delete-asset-storage-group-member-confirm.title', 'i18n.base.user-management.messages.delete-asset-storage-group-member-confirm.message', () => {
          this.userManagementService.deleteAssetStorageGroupMember(user.userId).subscribe(() => {
            this.grid.refresh();
          });
        });
      }
    }
  }

  public override actionBegin(args) {
    if (args.requestType === 'beginEdit') {
      if ((args.rowData as UserInfoDTO).roles.some(r => this.roleService.roleMappingInfo.fixedUserRoles.indexOf(r) > -1)) {
        args.cancel = true;
        return;
      }
    }

    if (args.requestType === SfRequestTypes.FilterBeforeOpen && args.columnName === this.rolesFieldName) {
      args.filterModel.options.dataSource = new EnumFilterDataManager<RoleEnumFilterType>(this.rolesEnumFilterValues);
    }
    if (args.requestType === SfRequestTypes.FilterChoiceRequest && args.filterModel.options.field === this.rolesFieldName) {
      args.query.distincts = []; // will set custom trigger flag and avoid filtering the list for distinct values of the enum filter field
      args.filterModel.options.dataManager = null;
    }

    super.actionBegin(args);
  }

  public override actionComplete(args) {
    super.actionComplete(args);
    if (args.requestType === 'beginEdit') {
      const data = args.rowData as UserInfoDTO;
      if (data.roles.includes(Role.AssetStorageGroupMember)) {
        const dialogInstance = (args as any).dialog;
        dialogInstance.buttons = [
          {
            buttonModel: { content: '<span class="e-btn-icon e-icons icon-mail--all font-larger"></span>', cssClass: 'left-align' },
            click: () => {
              this.resendEmail(data.userId);
            }
          },
          ...dialogInstance.buttons
        ];
      }
    }
  }

  private resendEmail(userId: string) {
    this.grid?.editModule.closeEdit();
    this.userManagementService.resendOnboardingEmail(userId).subscribe();
  }

  protected override getDataToSave(): any {
    return this.mapToUserInfoDTO(this.gridEditForm);
  }

  protected initializeEditForm(data: UserInfoDTO): void {
    // grid always creates dummy object for client
    if (!data.client?.id) {
      data.client = null;
    }

    const client = this.authService.getUserInfo().client
      ? this.authService.getUserInfo().client
      : data.client ? data.client : this.selectedClient;

    this.gridEditForm = this.fb.group<UserEditFormType>({
      userId: this.fb.control<string>({value: data.userId, disabled: true}),
      firstname: this.fb.control<string>({value: data.firstname, disabled: true}, [Validators.required]),
      lastname: this.fb.control<string>({value: data.lastname, disabled: true}, [Validators.required]),
      language: this.fb.control<string>({value: data.language, disabled: true}, [Validators.required]),
      email: this.fb.control<string>({value: data.email, disabled: true}, [Validators.required, Validators.email]),
      phoneNumber: this.fb.control<string>({value: data.phoneNumber, disabled: true}, [CustomValidatorsFactory.createPhoneValidator()]),
      enabled: this.fb.control<boolean>(data.enabled ?? false),
      client: this.fb.control<ClientDTO>({ value: client, disabled: true }, [Validators.required]),
      roles: this.fb.control<Role[]>({value: !!data.roles?.length ? data.roles : [], disabled: true}),
      userName: this.fb.control<string>({value: data.userName, disabled: true}, [Validators.required, CustomValidatorsFactory.createNoDomainUserValidation(), CustomValidatorsFactory.createNoWhiteSpacesAllowed()]),
      password: this.fb.control<string>({value: data.password, disabled: true}, [Validators.required, passwordValidation(), CustomValidatorsFactory.createNoWhiteSpacesAllowed()])
    });

    if (!data.userId) {
      this.gridEditForm.controls.roles.setValue([this.addUserRole]);
    }
  }

  protected getEditDialogTitleParams(rowData): object {
    return {};
  }

  private mapToUserInfoDTO(form: FormGroup<UserEditFormType>): UpdateUserInfoDTO | CreateUserDTO | UpdateInterfaceUserInfoDTO | CreateInterfaceUserDTO {
    const formData = form.getRawValue();
    let dto = null;
    var isAdmin = formData.roles?.includes(Role.Admin)
    if (formData.roles.includes(Role.InterfaceServiceAccess)) {
      if (!formData.userId) {
        dto = new CreateInterfaceUserDTO();
      } else {
        dto = new UpdateInterfaceUserInfoDTO();
      }
      dto.userName = formData.userName;
      dto.password = formData.password;
    } else {
      if (!formData.userId) {
        dto = new CreateUserDTO();
      } else {
        dto = new UpdateUserInfoDTO();
      }
    }
    dto.firstname = formData.firstname
    dto.lastname = formData.lastname;
    dto.userId = formData.userId;
    dto.clientId = isAdmin ? null : formData.client?.id;
    dto.email = formData.email;
    dto.enabled = formData.enabled;
    dto.language = formData.language;
    dto.phoneNumber = formData.phoneNumber;
    dto.roles = formData.roles;

    if ( formData.roles.includes(Role.AssetStorageGroupMember)) {
      dto.clientId = null;
    }
    return dto;
  }
}
